import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { WorktimeApiService } from '../../../core/services/worktime.service';
import { NotificationService } from '../../../core/services/notification.service';
import { DatabaseBackupService } from '../../../core/services/database-backup.service';
import { Permission } from '../../../core/enums/permission.enum';
import { PermissionService } from '../../../core/services/permission.service';
import { CampaignService } from '../../../core/services/campaigns.service';

@Component({
  selector: 'em-training-batch-settings',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
  ],
  templateUrl: './training-batch-settings.component.html',
  styleUrl: './training-batch-settings.component.scss',
})
export class TrainingBatchSettingsComponent {
  trainingBatchLoading = false;
  contractNotificationLoading = false;
  canTriggerContractNotification = false;
  poolBatchLoading = false;
  backupLoading = false;
  evaluationBatchLoading = false;

  constructor(
    private readonly worktimeService: WorktimeApiService,
    private readonly messageService: MessageService,
    private readonly notificationService: NotificationService,
    private readonly permissionService: PermissionService,
    private readonly translate: TranslateService,
    private readonly databaseBackupService: DatabaseBackupService,
    private readonly campaignService: CampaignService,
  ) {
    this.canTriggerContractNotification = this.permissionService.hasPermission(Permission.CONTRACT_ENDING_NOTIFICATION);
  }

  triggerTrainingBatch(): void {
    this.trainingBatchLoading = true;
    this.worktimeService.triggerTrainingBatchSync().subscribe({
      next: (results: any[]) => {
        this.trainingBatchLoading = false;
        const successCount = results.filter((r: any) => r.status === 'SUCCESS').length;
        const failCount = results.filter((r: any) => r.status === 'FAILED').length;
        const totalImported = results
          .filter((r: any) => r.status === 'SUCCESS')
          .reduce((sum: number, r: any) => sum + (r.importedCount || 0), 0);

        if (results.length === 0) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('layout.configurations.training_batch.toast.no_files_title'),
            detail: this.translate.instant('layout.configurations.training_batch.toast.no_files_detail'),
          });
        } else if (failCount === 0) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('layout.configurations.training_batch.toast.success_title'),
            detail: this.translate.instant('layout.configurations.training_batch.toast.success_detail', {
              successCount,
              totalImported,
            }),
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('layout.configurations.training_batch.toast.partial_title'),
            detail: this.translate.instant('layout.configurations.training_batch.toast.partial_detail', {
              successCount,
              failCount,
            }),
          });
        }
      },
      error: (error: any) => {
        this.trainingBatchLoading = false;
        let detail = this.translate.instant('layout.configurations.training_batch.toast.error_detail');
        if (error?.status === 403) {
          detail = this.translate.instant('layout.configurations.training_batch.toast.forbidden_error');
        } else if (error?.error?.message) {
          detail = error.error.message;
        }
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.training_batch.toast.error_title'),
          detail,
        });
      },
    });
  }
  
  triggerLifecycleNotifications(): void {
    this.contractNotificationLoading = true;
    this.notificationService.triggerCddNotification().subscribe({
      next: () => {
        this.contractNotificationLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.training_batch.lifecycle_notifications.toast.success_title'),
          detail: this.translate.instant('layout.configurations.training_batch.lifecycle_notifications.toast.success_detail'),
        });
      },
      error: () => {
        this.contractNotificationLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.training_batch.toast.error_title'),
          detail: this.translate.instant('layout.configurations.training_batch.lifecycle_notifications.toast.error_detail'),
        });
      },
    });
  }
  triggerPoolBatch(): void {
  this.poolBatchLoading = true;

  this.worktimeService.triggerPoolBatchSync().subscribe({
    next: () => {
      this.poolBatchLoading = false;

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant(
          'layout.configurations.training_batch.pool_batch.toast.success_title'
        ),
        detail: this.translate.instant(
          'layout.configurations.training_batch.pool_batch.toast.success_detail'
        ),
      });
    },

    error: (error: any) => {
      this.poolBatchLoading = false;

      let detail = this.translate.instant(
        'layout.configurations.training_batch.pool_batch.toast.error_detail'
      );

      if (error?.status === 403) {
        detail = this.translate.instant(
          'layout.configurations.training_batch.toast.forbidden_error'
        );
      } else if (error?.error?.message) {
        detail = error.error.message;
      }

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          'layout.configurations.training_batch.pool_batch.toast.error_title'
        ),
        detail,
      });
    },
  });
}
triggerEvaluationBatch(): void {
  this.evaluationBatchLoading = true;

  this.campaignService.triggerEvaluationBatch().subscribe({
    next: () => {
      this.evaluationBatchLoading = false;

      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('layout.configurations.training_batch.evaluation_batch.toast.success_title'),
        detail: this.translate.instant('layout.configurations.training_batch.evaluation_batch.toast.success_detail'),
      });
    },
    error: (error: any) => {
      this.evaluationBatchLoading = false;

      let detail = this.translate.instant('layout.configurations.training_batch.evaluation_batch.toast.error_detail');

      if (error?.status === 403) {
        detail = this.translate.instant('layout.configurations.training_batch.evaluation_batch.toast.forbidden_error');
      } else if (error?.error?.message) {
        detail = error.error.message;
      }

      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('layout.configurations.training_batch.evaluation_batch.toast.error_title'),
        detail,
      });
    },
  });
}
  triggerDatabaseBackup(): void {
    this.backupLoading = true;

    this.databaseBackupService.triggerDatabaseBackup().subscribe({
      next: (result) => {
        this.backupLoading = false;

        if (result.success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant(
              'layout.configurations.database_backup.toast.success_title'
            ),
            detail: this.translate.instant(
              'layout.configurations.database_backup.toast.success_detail'
            ),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant(
              'layout.configurations.database_backup.toast.error_title'
            ),
            detail: result.message || this.translate.instant(
              'layout.configurations.database_backup.toast.error_detail'
            ),
          });
        }
      },
      error: (error: any) => {
        this.backupLoading = false;

        let detail = this.translate.instant(
          'layout.configurations.database_backup.toast.error_detail'
        );

        if (error?.status === 403) {
          detail = this.translate.instant(
            'layout.configurations.database_backup.toast.forbidden_error'
          );
        } else if (error?.status === 400 && error?.error?.message) {
          detail = this.translate.instant(
            'layout.configurations.database_backup.toast.disabled_error'
          );
        } else if (error?.error?.message) {
          detail = error.error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'layout.configurations.database_backup.toast.error_title'
          ),
          detail,
        });
      },
    });
  }
}
